import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const LINES = [
    { text: "Prima di capire,", accent: false },
    { text: "spesso reagiamo.", accent: true },
];

export const Hero = () => {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 60]);

    return (
        <section id="hero" ref={ref} className="relative overflow-hidden bg-cream" data-testid="hero-section">
            <div className="pointer-events-none absolute -top-32 -right-32 h-[480px] w-[480px] rounded-full bg-sage-light/70 blur-3xl hidden md:block" aria-hidden="true" />
            <div className="pointer-events-none absolute bottom-0 -left-40 h-[380px] w-[380px] rounded-full bg-terracotta-light/60 blur-3xl hidden md:block" aria-hidden="true" />

            <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 pt-16 pb-20 md:pt-24 md:pb-32 grid lg:grid-cols-[1.15fr_1fr] gap-14 lg:gap-20 items-center">
                <div>
                    <motion.p
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }}
                        className="text-xs sm:text-sm font-semibold uppercase tracking-[0.25em] text-sage mb-6"
                        data-testid="hero-eyebrow"
                    >
                        Psicologo e Psicoterapeuta Dott. Giovanni De Lorenzo
                    </motion.p>

                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight text-ink" data-testid="hero-title">
                        {LINES.map((line, i) => (
                            <span key={i} className="block overflow-hidden pb-1">
                                <motion.span
                                    className={`block ${line.accent ? "italic text-petrol" : ""}`}
                                    initial={{ y: "110%" }}
                                    animate={{ y: 0 }}
                                    transition={{ duration: 0.9, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    {line.text}
                                </motion.span>
                            </span>
                        ))}
                    </h1>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.7 }}
                        className="mt-7 max-w-xl space-y-4 text-base sm:text-lg text-[#526063] leading-relaxed"
                        data-testid="hero-subtitle"
                    >
                        <p>
                            Ci sono momenti in cui sappiamo cosa dovremmo fare, ma facciamo altro. Ci arrabbiamo, ci allontaniamo, evitiamo, cerchiamo rassicurazioni, prendiamo decisioni che poi non comprendiamo fino in fondo.
                        </p>
                        <p>
                            Non sempre è una questione di volontà. A volte, prima del pensiero consapevole, qualcosa è già accaduto: nel corpo, nelle emozioni, nelle relazioni.
                        </p>
                        <p className="text-ink font-medium">
                            La psicoterapia, per me, significa partire da lì. Capire cosa sta succedendo. Comprendere perché accade. Imparare a scegliere, invece di reagire.
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.9 }}
                        className="mt-10 flex flex-col sm:flex-row gap-4"
                    >
                        <button
                            data-testid="hero-primary-cta"
                            onClick={() => scrollToId("#contatti")}
                            className="group inline-flex items-center justify-center gap-2 rounded-full bg-petrol text-cream px-8 py-4 text-base font-semibold hover:bg-petrol-soft hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
                        >
                            Prenota un primo colloquio
                            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                        </button>
                        <button
                            data-testid="hero-secondary-cta"
                            onClick={() => scrollToId("#approccio")}
                            className="inline-flex items-center justify-center gap-2 rounded-full border border-ink/20 bg-transparent text-ink px-8 py-4 text-base font-semibold hover:border-petrol hover:text-petrol hover:bg-petrol-light/40 transition-all duration-300"
                        >
                            Conosci il mio approccio
                            <ArrowDown size={18} />
                        </button>
                    </motion.div>

                    <motion.p
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 1, delay: 1.15 }}
                        className="mt-8 flex items-center gap-3 text-sm text-ink/60 italic font-serif"
                        data-testid="hero-reassurance"
                    >
                        <span className="h-px w-10 bg-sage" aria-hidden="true" />
                        Non devi cambiare chi sei. Devi poter comprendere ciò che ti accade.
                    </motion.p>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="relative mx-auto w-full max-w-md lg:max-w-none"
                >
                    <motion.div style={{ y: imgY }} className="relative">
                        <div className="overflow-hidden rounded-t-[999px] rounded-b-3xl shadow-2xl shadow-petrol/20 aspect-[4/5] bg-sand">
                            <img
                                src="/images/foto5.jpg"
                                alt="Ritratto professionale del Dott. Giovanni De Lorenzo, psicologo e psicoterapeuta"
                                className="h-full w-full object-cover"
                                loading="eager"
                                data-testid="hero-portrait"
                            />
                        </div>
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 1.1 }}
                            className="absolute -bottom-6 -left-4 sm:-left-8 rounded-2xl bg-white/90 backdrop-blur-md px-6 py-4 shadow-xl border border-ink/5"
                        >
                            <p className="font-serif italic text-lg text-petrol leading-snug">
                                "Imparare a scegliere,<br />invece di reagire."
                            </p>
                        </motion.div>
                    </motion.div>
                </motion.div>
            </div>
        </section>
    );
};
